/**
 * @file components/audit-tasks/field/AuditEvidenceAttachmentsCard.tsx
 * @description 現場稽核「Z附件」批量上傳與管理卡片。
 *
 * 稽核員可將文審階段尚未取得的資料、現場發現問題的補充證據等一次選取多檔上傳。
 * 附件以 case_documents.docType='Z附件' 統一歸檔，因此會與文審應附文件一起進入
 * 報告核准時凍結的 dossier_snapshot，供驗證決定小組在「稽核文件包」中查閱。
 */

import { useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  type UploadFile,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Paperclip, Trash2 } from 'lucide-react';
import { FileLink } from '@/components/common/FileLink';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { apiClient } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import { expandZipFiles, uploadFiles, type UploadedFile } from '@/lib/file-upload';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';

/** 現場補充證據的固定歸檔類型；決定端 dossier 會以此類型展示。 */
const AUDIT_EVIDENCE_DOC_TYPE = 'Z附件';

/** 批量關聯端點與 files 批量 confirm 共用的單批上限。 */
const MAX_BATCH_FILES = 100;

/** GET /api/cases/:id 回傳的案件附件欄位（僅列出本卡片所需資料）。 */
interface CaseDocument {
  id: number;
  docType: string;
  fileId: number;
  filename: string | null;
  sizeBytes: number | null;
  mimeType: string | null;
  createdAt: string;
}

/** GET /api/cases/:id 的局部 response shape。 */
interface CaseDetailResponse {
  item: {
    documents: CaseDocument[];
  };
}

interface Props {
  /** cases.id */
  caseId: number;
  /** 是否允許現場寫入；管理組現場視角為唯讀。 */
  canWrite: boolean;
}

/** 將位元組格式化為適合表格展示的大小。 */
function formatSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * 刪除批量上傳成功、但案件關聯失敗的孤立 files 記錄。
 * 清理失敗只記錄前端錯誤，不覆蓋原始關聯錯誤，方便使用者看到真正失敗原因。
 */
async function cleanupUnlinkedFiles(files: UploadedFile[]): Promise<void> {
  const results = await Promise.allSettled(
    files.map((file) => apiClient.delete(`/api/files/${file.id}`)),
  );
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error('[AuditEvidenceAttachmentsCard] cleanup unlinked file failed', {
        fileId: files[index]?.id,
        err: result.reason,
      });
    }
  });
}

/** 現場補充證據卡片：批量選檔、上傳、下載與誤傳刪除。 */
export function AuditEvidenceAttachmentsCard({ caseId, canWrite }: Props) {
  const qc = useQueryClient();
  const { isOnline } = useNetworkStatus();
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  const { data, isLoading } = useQuery<CaseDetailResponse>({
    queryKey: ['case-detail-documents', caseId],
    queryFn: async () => {
      const response = await fetchWithOfflineCache<CaseDetailResponse>(`/api/cases/${caseId}`, {
        scope: 'case-detail',
      });
      return response.data;
    },
  });

  /** 本卡片只展示現場補充證據；文審應附文件仍在準備階段的應附文件卡片中維護。 */
  const evidenceDocuments = (data?.item.documents ?? []).filter(
    (document) => document.docType === AUDIT_EVIDENCE_DOC_TYPE,
  );

  /** 同步刷新現場卡片與其他案件詳情消費者。 */
  const invalidateDocuments = () => {
    void qc.invalidateQueries({ queryKey: ['case-detail-documents', caseId] });
    void qc.invalidateQueries({ queryKey: ['case-detail', caseId] });
  };

  /**
   * 批量上傳流程：展開 zip → OSS 併發直傳/批量 confirm → 單一事務關聯案件。
   * 單檔上傳失敗不會阻斷其餘文件；成功文件仍會歸檔，並明確提示失敗文件需重傳。
   */
  const handleBatchUpload = async () => {
    const pickedFiles = selectedFiles.reduce<File[]>((files, selectedFile) => {
      if (selectedFile.originFileObj) files.push(selectedFile.originFileObj);
      return files;
    }, []);
    if (pickedFiles.length === 0) {
      message.warning('請先選擇至少一個附件');
      return;
    }

    setUploading(true);
    setUploadStatus('正在準備附件…');
    try {
      const expandedFiles = await expandZipFiles(pickedFiles);
      if (expandedFiles.length === 0) {
        throw new Error('所選壓縮包內沒有可上傳的附件');
      }
      if (expandedFiles.length > MAX_BATCH_FILES) {
        throw new Error(`單次最多上傳 ${MAX_BATCH_FILES} 個附件，請分批處理`);
      }

      const result = await uploadFiles(expandedFiles, {
        scope: 'case',
        ownerType: 'case',
        ownerId: caseId,
        onProgress: (done, total, current) => {
          setUploadStatus(`上傳中 ${done}/${total}：${current}`);
        },
      });
      const uploadedFiles = result.succeeded;

      if (uploadedFiles.length > 0) {
        setUploadStatus('正在歸檔至稽核文件包…');
        try {
          await apiClient.post(`/api/cases/${caseId}/documents/batch`, {
            fileIds: uploadedFiles.map((file) => file.id),
            docType: AUDIT_EVIDENCE_DOC_TYPE,
            note: '現場稽核補充證據',
          });
        } catch (err) {
          // 關聯失敗時軟刪除尚未關聯的 files 元資料，避免列表外留下不可見孤立記錄。
          await cleanupUnlinkedFiles(uploadedFiles);
          throw err;
        }
      }

      setSelectedFiles([]);
      invalidateDocuments();
      if (result.failed.length === 0) {
        message.success(`已將 ${uploadedFiles.length} 個附件歸檔至稽核文件包`);
      } else {
        const failedNames = result.failed.map((failure) => failure.file.name).join('、');
        message.warning(
          `已歸檔 ${uploadedFiles.length} 個，另有 ${result.failed.length} 個失敗：${failedNames}`,
        );
      }
    } catch (err) {
      console.error('[AuditEvidenceAttachmentsCard] batch upload failed', { caseId, err });
      message.error(err instanceof Error ? err.message : '附件上傳失敗');
    } finally {
      setUploading(false);
      setUploadStatus(null);
    }
  };

  /** 誤傳刪除：同時移除案件關聯並軟刪除 files 元資料。 */
  const removeMutation = useMutation({
    mutationFn: (documentId: number) =>
      apiClient.delete(`/api/cases/${caseId}/documents/${documentId}?softDeleteFile=true`),
    onSuccess: () => {
      message.success('附件已刪除');
      invalidateDocuments();
    },
    onError: (err: Error) => {
      console.error('[AuditEvidenceAttachmentsCard] remove evidence failed', { caseId, err });
      message.error(err.message || '附件刪除失敗');
    },
  });

  const columns: ColumnsType<CaseDocument> = [
    {
      title: '檔名',
      dataIndex: 'filename',
      ellipsis: true,
      render: (filename: string | null, document) =>
        filename ? <FileLink fileId={document.fileId} filename={filename} compact /> : '—',
    },
    {
      title: '大小',
      dataIndex: 'sizeBytes',
      width: 110,
      render: formatSize,
    },
    {
      title: '上傳時間',
      dataIndex: 'createdAt',
      width: 180,
      render: (createdAt: string) => new Date(createdAt).toLocaleString('zh-TW'),
    },
    ...(canWrite
      ? ([
          {
            title: '操作',
            key: 'actions',
            width: 80,
            render: (_: unknown, document: CaseDocument) => (
              <Tooltip title={isOnline ? '刪除誤傳附件' : '離線狀態無法刪除'}>
                <Popconfirm
                  title="刪除此附件？"
                  description="刪除後不會進入後續稽核文件包快照。"
                  okText="刪除"
                  cancelText="取消"
                  disabled={!isOnline}
                  onConfirm={() => removeMutation.mutate(document.id)}
                >
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<Trash2 size={14} />}
                    disabled={!isOnline}
                    loading={removeMutation.isPending && removeMutation.variables === document.id}
                  />
                </Popconfirm>
              </Tooltip>
            ),
          },
        ] satisfies ColumnsType<CaseDocument>)
      : []),
  ];

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <Paperclip size={16} />
          <span>現場補充證據（Z附件）</span>
          <Tag>{evidenceDocuments.length}</Tag>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          可上傳文審時尚未提供的資料，以及現場發現問題所需的照片、文件或其他證據；附件將與應附文件統一歸入稽核文件包。
        </Typography.Text>

        {canWrite && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Upload
              multiple
              fileList={selectedFiles}
              maxCount={MAX_BATCH_FILES}
              disabled={!isOnline || uploading}
              beforeUpload={() => false}
              onChange={({ fileList }) => setSelectedFiles(fileList.slice(0, MAX_BATCH_FILES))}
            >
              <Tooltip title={isOnline ? undefined : '離線狀態無法上傳附件'}>
                <Button icon={<Paperclip size={14} />} disabled={!isOnline || uploading}>
                  選擇多個附件
                </Button>
              </Tooltip>
            </Upload>
            <Space>
              <Button
                type="primary"
                icon={<FileUp size={14} />}
                disabled={!isOnline || selectedFiles.length === 0}
                loading={uploading}
                onClick={() => void handleBatchUpload()}
              >
                上傳所選附件{selectedFiles.length > 0 ? `（${selectedFiles.length}）` : ''}
              </Button>
              {uploadStatus && <Typography.Text type="secondary">{uploadStatus}</Typography.Text>}
            </Space>
          </Space>
        )}

        {isLoading ? (
          <Typography.Text>載入中…</Typography.Text>
        ) : evidenceDocuments.length > 0 ? (
          <Table<CaseDocument>
            rowKey="id"
            dataSource={evidenceDocuments}
            columns={columns}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚無現場補充附件" />
        )}
      </Space>
    </Card>
  );
}
